'use es6';
/**
 * SHAPE
 *
  {
    order: [123, 124, 125],
    widgets: {
      123: CustomWidget,
      124: CustomWidget,
      125: CustomWidget,
    },
    isInitialized: true,
    fetchStatus: SUCCEEDED,
  }
*/

import SeamlessImmutable from 'seamless-immutable';
import { sortBy, without } from 'underscore';
import { UNINITIALIZED, PENDING, FAILED, SUCCEEDED } from 'DesignData/enums/RequestStatus';
import { CustomWidget as CustomWidgetWidget } from 'DesignData/models/widgets';
import { CUSTOM_WIDGET } from 'DesignData/enums/FolderAssetTypes';
import { FINDER_FILE_RENAME, CUSTOM_WIDGETS_WITH_META_DATA_FETCH, CUSTOM_WIDGET_BUFFER_DELETE, CUSTOM_WIDGET_BUFFER_PUBLISH, CUSTOM_WIDGET_CREATE, CUSTOM_WIDGET_CREATE_FROM_FILE, CUSTOM_WIDGETS_MAPPING_FETCH, LAYOUT_WIDGET_RESTORE_PARENT, LAYOUT_WIDGET_MAKE_CMV2_GLOBAL, LAYOUT_WIDGET_MAKE_LOCAL_COPY_CMV2_GLOBAL_WIDGET, GLOBAL_WIDGET_UPGRADE } from 'DesignData/actions/ActionTypes';

const getLabel = widget => widget.label;

const getId = widget => widget.id;

function getSortedOrder(widgets) {
  return sortBy(widgets, getLabel).map(getId);
}

function addWidget(state, customWidget) {
  const widgets = state.widgets.set(customWidget.id, new CustomWidgetWidget(customWidget));
  const order = getSortedOrder(widgets);
  return state.merge({
    order,
    widgets
  });
}

export default function (state = SeamlessImmutable({
  order: [],
  widgets: {},
  fetchStatus: UNINITIALIZED,
  isInitialized: false,
  mapping: {},
  mappingFetchStatus: UNINITIALIZED,
  isMappingInitialized: false
}), action) {
  switch (action.type) {
    case CUSTOM_WIDGETS_WITH_META_DATA_FETCH:
      {
        const {
          customWidgets,
          error
        } = action;

        if (!(customWidgets || error)) {
          return state.set('fetchStatus', PENDING);
        }

        if (error) {
          return state.set('fetchStatus', FAILED);
        }

        const order = getSortedOrder(customWidgets);
        const widgets = {};
        customWidgets.forEach(cw => {
          widgets[cw.id] = new CustomWidgetWidget(cw);
        });
        return SeamlessImmutable.merge(state, {
          order,
          widgets,
          fetchStatus: SUCCEEDED,
          isInitialized: true
        });
      }

    case CUSTOM_WIDGETS_MAPPING_FETCH:
      {
        const {
          mapping,
          error
        } = action;

        if (error) {
          return SeamlessImmutable.set(state, 'mappingFetchStatus', FAILED);
        }

        if (!mapping) {
          return SeamlessImmutable.set(state, 'mappingFetchStatus', PENDING);
        }

        return SeamlessImmutable.merge(state, {
          mapping,
          isMappingInitialized: true,
          mappingFetchStatus: SUCCEEDED
        });
      }

    case CUSTOM_WIDGET_CREATE:
    case CUSTOM_WIDGET_CREATE_FROM_FILE:
    case CUSTOM_WIDGET_BUFFER_PUBLISH:
    case LAYOUT_WIDGET_MAKE_CMV2_GLOBAL:
    case LAYOUT_WIDGET_MAKE_LOCAL_COPY_CMV2_GLOBAL_WIDGET:
    case GLOBAL_WIDGET_UPGRADE:
      {
        const {
          customWidget
        } = action;

        if (!customWidget) {
          return state;
        }

        return addWidget(state, customWidget);
      }

    case FINDER_FILE_RENAME:
      {
        const {
          fileId,
          file,
          fileType
        } = action;

        if (!file || !fileId || !fileType) {
          return state;
        }

        if (fileType !== CUSTOM_WIDGET) {
          return state;
        }

        if (!state.widgets[fileId]) {
          return state;
        }

        const widgets = state.widgets.setIn([fileId, 'name'], file.name).setIn([fileId, 'label'], file.label).setIn([fileId, 'path'], file.path);
        const order = getSortedOrder(widgets);
        return state.merge({
          order,
          widgets
        });
      }

    case CUSTOM_WIDGET_BUFFER_DELETE:
      {
        const {
          customWidgetId
        } = action;
        const widgetsToDelete = Array.isArray(customWidgetId) ? customWidgetId : [customWidgetId];
        return state.merge({
          order: without(state.order, ...widgetsToDelete),
          widgets: state.widgets.without(widgetsToDelete)
        });
      }

    case LAYOUT_WIDGET_RESTORE_PARENT:
      {
        const {
          widgetType,
          parent: customWidget
        } = action;

        if (!(widgetType === 'custom_widget' && customWidget)) {
          return state;
        }

        return addWidget(state, customWidget);
      }

    default:
      return state;
  }
}